import { z } from 'zod'
import { FOLLOW_UP_AFTER_DAYS } from './compose-suggestions.js'
import { deriveWorklistStatus, type WorklistInput } from './job-worklist.js'
import { paginationSchema } from './list-params.js'

/**
 * Billing: every quote still to win and every job still to be paid for, read
 * from where the *job* is rather than from what its documents say.
 *
 * The Quotes and Invoices screens listed documents, and a document's status
 * goes stale the moment the job moves on without it: a quote marked Sent on a
 * job that was booked by hand three weeks ago still read "Awaiting reply", and
 * every ServiceM8 work order arrived with a draft invoice that read "Draft".
 * So the lanes here are keyed by the job's stage, the same `Job.status` the
 * Jobs list's tabs read (decision 35):
 *
 * - **Quotes** is every job in the Quote stage that is not closed. It leaves the
 *   lane the moment it becomes a work order, whatever its quote says, because
 *   going ahead is the answer.
 * - **Invoices** starts where the work has been done and the customer holds no
 *   invoice, and ends when what they owe is paid.
 *
 * Within the quote lane, Draft and Priced are `deriveWorklistStatus`'s own, so
 * the job card's segmented bar and this lane cannot disagree (rule 29). Sent is
 * split three ways by what has happened since, which the worklist cannot see.
 *
 * Each rule is a pure function of a few facts the API gathers, so it is tested
 * here without a database and the API only has to collect the facts.
 */

export const PIPELINE_LANES = ['QUOTES', 'INVOICES'] as const
export type PipelineLane = (typeof PIPELINE_LANES)[number]

/** Tones from the web's five (decision 110). */
type StageTone = 'neutral' | 'outline' | 'green' | 'dark' | 'red'

interface StageDefinition {
  key: string
  lane: PipelineLane
  label: string
  /** What being here means, in a sentence. */
  meaning: string
  /** What to do about it, or null when the stage only waits. */
  action: string | null
  tone: StageTone
}

/**
 * In the order a job moves through them. Every open quote is in exactly one of
 * the first five; every unpaid job or invoice in exactly one of the last four.
 */
export const PIPELINE_STAGES = [
  {
    key: 'DRAFT',
    lane: 'QUOTES',
    label: 'Draft',
    meaning: 'Nothing priced on the quote yet',
    action: 'Price it',
    tone: 'neutral',
  },
  {
    key: 'PRICED',
    lane: 'QUOTES',
    label: 'Priced',
    meaning: 'Priced, not sent to the customer',
    action: 'Send it',
    tone: 'outline',
  },
  {
    key: 'SENT',
    lane: 'QUOTES',
    label: 'Sent',
    meaning: `With the customer, chased in the last ${FOLLOW_UP_AFTER_DAYS} days or due a follow-up later`,
    action: null,
    tone: 'outline',
  },
  {
    key: 'VIEWED',
    lane: 'QUOTES',
    label: 'Viewed',
    meaning: 'The customer has opened it online',
    action: 'Ring while it is warm',
    tone: 'dark',
  },
  {
    key: 'FOLLOW_UP',
    lane: 'QUOTES',
    label: 'Follow up',
    meaning: `No word for ${FOLLOW_UP_AFTER_DAYS} days, a follow-up has come due, or the customer wrote back`,
    action: 'Follow up',
    tone: 'red',
  },
  {
    key: 'VISITED',
    lane: 'INVOICES',
    label: 'Visited',
    meaning: 'Been on site, not marked complete, not invoiced',
    action: 'Invoice it',
    tone: 'dark',
  },
  {
    key: 'TO_INVOICE',
    lane: 'INVOICES',
    label: 'To invoice',
    meaning: 'Work complete, the customer has no invoice',
    action: 'Invoice it',
    tone: 'dark',
  },
  {
    key: 'OWING',
    lane: 'INVOICES',
    label: 'Owing',
    meaning: 'Invoiced, not due yet',
    action: null,
    tone: 'outline',
  },
  {
    key: 'OVERDUE',
    lane: 'INVOICES',
    label: 'Overdue',
    meaning: 'Past its due date with money still owing',
    action: 'Chase it',
    tone: 'red',
  },
] as const satisfies ReadonlyArray<StageDefinition>

export type PipelineStageKey = (typeof PIPELINE_STAGES)[number]['key']
export type QuoteStageKey = Extract<
  PipelineStageKey,
  'DRAFT' | 'PRICED' | 'SENT' | 'VIEWED' | 'FOLLOW_UP'
>
export type UnbilledStageKey = Extract<PipelineStageKey, 'VISITED' | 'TO_INVOICE'>
export type OwingStageKey = Extract<PipelineStageKey, 'OWING' | 'OVERDUE'>

export const PIPELINE_STAGE_KEYS = PIPELINE_STAGES.map((stage) => stage.key) as [
  PipelineStageKey,
  ...PipelineStageKey[],
]

export function pipelineStage(key: PipelineStageKey): (typeof PIPELINE_STAGES)[number] {
  const found = PIPELINE_STAGES.find((stage) => stage.key === key)
  if (!found) throw new Error(`Unknown pipeline stage: ${key}`)
  return found
}

export function stagesInLane(lane: PipelineLane): PipelineStageKey[] {
  return PIPELINE_STAGES.filter((stage) => stage.lane === lane).map((stage) => stage.key)
}

/** The Quote stage without Unsuccessful: a closed job is no longer a quote to win. */
export const OPEN_QUOTE_JOB_STATUSES = ['LEAD', 'QUOTING', 'QUOTED'] as const
/** The Work Order stage, as the Jobs list's tab has it. */
export const WORK_ORDER_JOB_STATUSES = [
  'APPROVED',
  'SCHEDULED',
  'IN_PROGRESS',
  'ON_HOLD',
  'AWAITING_PARTS',
] as const
/** An invoice that still asks for money. */
export const OWED_INVOICE_STATUSES = ['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'] as const

const DAY = 24 * 60 * 60 * 1000

type When = Date | string | null | undefined

const at = (value: Date | string): Date => (value instanceof Date ? value : new Date(value))

/** The later of some dates, ignoring the missing ones. */
export function latestOf(...values: When[]): Date | null {
  let latest: Date | null = null
  for (const value of values) {
    if (value === null || value === undefined) continue
    const date = at(value)
    if (latest === null || date > latest) latest = date
  }
  return latest
}

/**
 * A job's dated follow-up is a `Task` (rule 20: job-card follow-ups stay
 * Task) whose title says so. The office writes them as "Follow up …" or
 * "Chase …", and so does the follow-up window, so a hand-made "Follow up with
 * Bob about the price" on Friday counts as well — which is what it is.
 */
export const FOLLOW_UP_TASK_PREFIXES = ['Follow up', 'Follow-up', 'Chase'] as const

export function isFollowUpTask(title: string): boolean {
  const lower = title.trim().toLowerCase()
  return FOLLOW_UP_TASK_PREFIXES.some((prefix) => lower.startsWith(prefix.toLowerCase()))
}

export function followUpTaskTitle(kind: 'quote' | 'invoice', number: string | null): string {
  if (kind === 'invoice') return number ? `Chase invoice ${number}` : 'Chase the invoice'
  return number ? `Follow up quote ${number}` : 'Follow up the quote'
}

/**
 * What happened when somebody followed up, for the feed note. A text is here
 * because the phone sends it and FieldOps never learns it went; an email is
 * not, because the message on the job is its own record.
 */
export const FOLLOW_UP_OUTCOMES = [
  { key: 'SPOKE', label: 'Spoke to them' },
  { key: 'NO_ANSWER', label: 'No answer' },
  { key: 'LEFT_MESSAGE', label: 'Left a message' },
  { key: 'TEXTED', label: 'Sent a text' },
  { key: 'OTHER', label: 'Something else' },
] as const

export type FollowUpOutcomeKey = (typeof FOLLOW_UP_OUTCOMES)[number]['key']

export const FOLLOW_UP_OUTCOME_KEYS = FOLLOW_UP_OUTCOMES.map((outcome) => outcome.key) as [
  FollowUpOutcomeKey,
  ...FollowUpOutcomeKey[],
]

/**
 * How a logged follow-up's note starts. The note is the touch: the lane reads
 * the newest one as the last time the business reached the customer, so a
 * follow-up written down with no next date still restarts the wait.
 */
export const FOLLOW_UP_NOTE_PREFIXES = ['Followed up quote', 'Chased invoice'] as const

/**
 * The job note a logged follow-up leaves in the feed: "Followed up quote
 * QTE-00012: Spoke to them. Deciding after the weekend." The words are the
 * record; the next date is the task beside it.
 */
export function followUpNoteBody(
  kind: 'quote' | 'invoice',
  number: string | null,
  outcome: FollowUpOutcomeKey,
  note?: string | null,
): string {
  const what = kind === 'invoice' ? FOLLOW_UP_NOTE_PREFIXES[1] : FOLLOW_UP_NOTE_PREFIXES[0]
  const label = FOLLOW_UP_OUTCOMES.find((entry) => entry.key === outcome)?.label ?? outcome
  const said = note?.trim()
  return `${what}${number ? ` ${number}` : ''}: ${label}.${said ? ` ${said}` : ''}`
}

// ---------------------------------------------------------------------------
// Quotes

/** What the quote lane reads about one job. */
export interface QuoteStageInput extends Pick<WorklistInput, 'status' | 'quoteSentAt' | 'quotePriced'> {
  /** When the customer last opened a live quote of this job online. */
  viewedAt: When
  /**
   * The last time the business reached the customer about this job: a quote
   * sent or resent, an email or text recorded on the job, or a follow-up
   * logged as done.
   */
  lastTouchAt: When
  /** The customer's latest message about the job. */
  lastHeardAt: When
  /** The soonest open, dated follow-up on the job. */
  nextFollowUpAt: When
}

/** Why a quote is in Follow up, for the row to say. */
export type FollowUpReason = 'REPLIED' | 'DUE' | 'QUIET'

export interface QuoteStage {
  stage: QuoteStageKey
  /** Set only in Follow up. */
  why: FollowUpReason | null
}

/**
 * Where an open quote is. Null for a job that is not an open quote, which is
 * every job outside the Quote stage and every closed one.
 *
 * In order: not sent is Draft or Priced, as the worklist has it. Then a reply
 * from the customer since we last spoke wants an answer whatever else is true.
 * Then a dated follow-up decides on its own: ahead, the quote waits; come due,
 * it is to follow up. With none, it is the timer — `FOLLOW_UP_AFTER_DAYS`, the
 * same wait after which the compose window offers a follow-up.
 */
export function quoteStageOf(job: QuoteStageInput, now: Date = new Date()): QuoteStage | null {
  if (!(OPEN_QUOTE_JOB_STATUSES as readonly string[]).includes(job.status)) return null

  const worklist = deriveWorklistStatus(
    { ...job, lastVisitAt: null, invoiceSentAt: null, invoicePaid: false },
    now,
  )
  if (worklist === 'DRAFT') return { stage: 'DRAFT', why: null }
  if (worklist === 'PRICED') return { stage: 'PRICED', why: null }

  const touched = latestOf(job.lastTouchAt, job.quoteSentAt)
  const waiting: QuoteStage = { stage: job.viewedAt ? 'VIEWED' : 'SENT', why: null }

  if (job.lastHeardAt && (!touched || at(job.lastHeardAt) > touched)) {
    return { stage: 'FOLLOW_UP', why: 'REPLIED' }
  }

  if (job.nextFollowUpAt) {
    return at(job.nextFollowUpAt) > now ? waiting : { stage: 'FOLLOW_UP', why: 'DUE' }
  }

  if (!touched || now.getTime() - touched.getTime() >= FOLLOW_UP_AFTER_DAYS * DAY) {
    return { stage: 'FOLLOW_UP', why: 'QUIET' }
  }

  return waiting
}

// ---------------------------------------------------------------------------
// Invoices

/** One of a job's live invoices: not deleted, not void, not a credit note. */
export interface PipelineInvoiceInput {
  status: string
  sentAt: When
  /** Came from ServiceM8, so a missing `sentAt` says nothing (decision 79). */
  imported: boolean
  /** When known: an issued invoice for nothing is a job billed at no charge. */
  totalIncTax?: number
}

/**
 * Whether the customer holds this invoice.
 *
 * Paid or written off is settled business whatever else is true. One that is
 * issued and owing counts once it has been sent — or when it was imported,
 * because the library predates the send record and "never sent" would be a
 * guess. A draft never counts: it is the office's working copy.
 *
 * An issued invoice for nothing counts too. That is how a job done at no
 * charge — a callback, a warranty re-treatment — is closed off: a $0 line,
 * issued, and there is nothing to send or chase.
 */
export function customerHolds(invoice: PipelineInvoiceInput): boolean {
  if (invoice.status === 'PAID' || invoice.status === 'WRITTEN_OFF') return true
  if (!(OWED_INVOICE_STATUSES as readonly string[]).includes(invoice.status)) return false
  if (invoice.totalIncTax !== undefined && invoice.totalIncTax <= 0) return true
  return invoice.sentAt !== null && invoice.sentAt !== undefined ? true : invoice.imported
}

export interface UnbilledStageInput {
  status: string
  lastVisitAt: When
  invoices: readonly PipelineInvoiceInput[]
}

/**
 * Where a job is whose work has been done and that the customer has not been
 * billed for. Null for every other job.
 *
 * To invoice is a completed job. Visited is a work order whose last booking
 * has passed with nothing booked after it — the Jobs list's Follow-up — which
 * is how a job finished on site and never closed in the office looks. Either
 * way, the job leaves once the customer holds an invoice for it.
 */
export function unbilledStageOf(
  job: UnbilledStageInput,
  now: Date = new Date(),
): UnbilledStageKey | null {
  if (job.invoices.some(customerHolds)) return null
  if (job.status === 'COMPLETED') return 'TO_INVOICE'
  if (
    (WORK_ORDER_JOB_STATUSES as readonly string[]).includes(job.status) &&
    job.lastVisitAt &&
    at(job.lastVisitAt) <= now
  ) {
    return 'VISITED'
  }
  return null
}

/** What the office has to do to get an invoice into the customer's hands. */
export type UnbilledInvoiceState = 'NONE' | 'DRAFT' | 'UNSENT'

export function unbilledInvoiceState(invoices: readonly PipelineInvoiceInput[]): UnbilledInvoiceState {
  if (invoices.some((invoice) => (OWED_INVOICE_STATUSES as readonly string[]).includes(invoice.status))) {
    return 'UNSENT'
  }
  if (invoices.some((invoice) => invoice.status === 'DRAFT')) return 'DRAFT'
  return 'NONE'
}

export interface OwingStageInput extends PipelineInvoiceInput {
  totalIncTax: number
  amountPaid: number
  dueAt: When
  /** False for an invoice raised against a client with no job. */
  onJob: boolean
}

/**
 * Where an invoice is that still asks for money. Null when it asks for none.
 *
 * Overdue is the Invoices screen's rule: owing, and the due date has passed.
 * An invoice on a job that the customer does not hold yet is not here — its
 * job is in To invoice, because the next step is to send it, not to chase it.
 */
export function owingStageOf(invoice: OwingStageInput, now: Date = new Date()): OwingStageKey | null {
  if (!(OWED_INVOICE_STATUSES as readonly string[]).includes(invoice.status)) return null
  if (invoice.totalIncTax - invoice.amountPaid <= 0) return null
  if (invoice.onJob && !customerHolds(invoice)) return null
  if (invoice.dueAt && at(invoice.dueAt) < now) return 'OVERDUE'
  return 'OWING'
}

// ---------------------------------------------------------------------------
// The list's parameters

export const PIPELINE_SORTS = ['waiting', 'value', 'newest'] as const
export type PipelineSort = (typeof PIPELINE_SORTS)[number]

/**
 * `.catch` on each, like every list's schema: a stage or sort renamed later
 * falls back rather than failing a link somebody kept.
 */
export const pipelineListSchema = paginationSchema.extend({
  stage: z.enum(PIPELINE_STAGE_KEYS).catch('FOLLOW_UP').default('FOLLOW_UP'),
  q: z.string().trim().min(1).max(200).optional().catch(undefined),
  /** Longest waiting first, biggest first, or newest first. */
  sort: z.enum(PIPELINE_SORTS).catch('waiting').default('waiting'),
})

export type PipelineListParams = z.infer<typeof pipelineListSchema>

/** How long a follow-up can be put off from the follow-up window. */
export const FOLLOW_UP_LATER_DAYS = [3, 7, 14, 30] as const
